import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import { getBillingCycleReadinessDetails } from '../../services/api';
import type { BillingCycleReadinessDetailsResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';

/** The two draft tabs whose readiness details load lazily. */
export type DraftReadinessTab = 'not-ready' | 'ready';

/**
 * The caller's current list identity for one draft tab. A consumer passes its
 * own scope on every read and request, so the provider never has to guess which
 * list a row belonged to — and a draft preview or a paged snapshot can never
 * read another list's evidence.
 */
export type DraftReadinessScope = {
  tabId: DraftReadinessTab;
  queryKey: QueryKey;
  isPlaceholderData: boolean;
};

/**
 * One cycle's readiness-details read.
 *
 * `loading | success | error`; an absent entry means not requested or not
 * available for the caller's current scope.
 */
export type DetailEntry = {
  status: 'loading' | 'success' | 'error';
  promise?: Promise<void>;
  data?: BillingCycleReadinessDetailsResponse;
};

export type ClaimReadinessContextValue = {
  revision: number;
  registerList(scope: DraftReadinessScope): void;
  loadDetails(
    scope: DraftReadinessScope,
    cycleId: string,
    retry?: boolean
  ): Promise<void> | undefined;
  getEntry(
    scope: DraftReadinessScope,
    cycleId: string
  ): DetailEntry | undefined;
  /**
   * Whether this cycle belongs to the caller's current list, including a
   * same-query revalidation whose evidence must wait for the list to settle.
   *
   * `false` means an absent detail entry is unavailable, not a pending request.
   */
  isScopeAdmitted(scope: DraftReadinessScope, cycleId: string): boolean;
};

const DRAFT_READINESS_TABS: DraftReadinessTab[] = ['not-ready', 'ready'];

/**
 * One draft tab's current list lifetime.
 *
 * Membership and evidence belong to a single settled list generation
 * (`queryHash` + `dataUpdateCount`), so a stale fetch cannot write evidence into
 * a list it no longer describes. History is not retained: registering a
 * different key replaces the snapshot.
 */
type DraftReadinessSnapshot = {
  queryHash: string;
  queryKey: QueryKey;
  dataUpdateCount: number;
  available: boolean;
  rowIds: Set<string>;
  entries: Map<string, DetailEntry>;
};

const ClaimReadinessContext = createContext<ClaimReadinessContextValue | null>(
  null
);

// eslint-disable-next-line react-refresh/only-export-components -- the provider file also exposes its consumer hook and scope types
export function useClaimReadiness(): ClaimReadinessContextValue {
  const value = useContext(ClaimReadinessContext);
  if (!value) {
    throw new Error(
      'useClaimReadiness must be used within a ClaimReadinessProvider'
    );
  }
  return value;
}

/** The cycle IDs a list response describes. Tolerant of an unexpected shape. */
function collectRowIds(data: unknown): Set<string> {
  const items = (data as { items?: unknown } | undefined)?.items;
  const ids = new Set<string>();
  if (!Array.isArray(items)) return ids;
  for (const item of items) {
    const id = (item as { billing_cycle_id?: unknown } | null)
      ?.billing_cycle_id;
    if (typeof id === 'string') ids.add(id);
  }
  return ids;
}

const emptySnapshots = (): Record<
  DraftReadinessTab,
  DraftReadinessSnapshot | undefined
> => ({ 'not-ready': undefined, ready: undefined });

/**
 * Owns lazy readiness-detail reads for the two draft tabs.
 *
 * One instance lives above the draft tabs (see `EOEClaimsManager`). Each draft
 * tab registers its current list scope from a passive effect; unmounting a tab
 * does not erase that tab's current snapshot. A local QueryCache subscription
 * tracks the registered list keys so a new list generation drops stale
 * evidence, and reads/writes are guarded by the caller's scope plus the
 * snapshot/entry identity, so a late response never becomes current.
 */
export const ClaimReadinessProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const queryClient = useQueryClient();
  const agencyId = useStore(state => state.user?.agency?.id);

  const snapshots =
    useRef<Record<DraftReadinessTab, DraftReadinessSnapshot | undefined>>(
      emptySnapshots()
    );
  // StrictMode reuses refs across an effect replay, so the live flag is set in
  // the subscription effect rather than at initialization; a ref set once would
  // leave the replayed provider dead.
  const alive = useRef(true);
  const [revision, setRevision] = useState(0);
  const notify = useCallback(() => setRevision(value => value + 1), []);

  /**
   * The caller's exact snapshot, or `undefined` when the caller may not read.
   *
   * `getEntry` and `loadDetails` require a settled successful list. Only display
   * admission permits same-query revalidation; all callers still enforce the
   * live provider, known agency, non-placeholder scope, matching query hash,
   * list generation and row membership.
   */
  const currentSnapshot = useCallback(
    (
      scope: DraftReadinessScope,
      cycleId: string,
      allowRevalidation = false
    ) => {
      if (!alive.current || !agencyId || scope.isPlaceholderData) return;
      const snapshot = snapshots.current[scope.tabId];
      const query = queryClient.getQueryCache().find({
        queryKey: scope.queryKey,
        exact: true,
      });
      if (
        !snapshot ||
        !query ||
        snapshot.queryHash !== query.queryHash ||
        snapshot.dataUpdateCount !== query.state.dataUpdateCount ||
        query.state.status !== 'success' ||
        (!(allowRevalidation && query.state.fetchStatus === 'fetching') &&
          (!snapshot.available ||
            query.state.fetchStatus !== 'idle' ||
            query.state.isInvalidated)) ||
        !snapshot.rowIds.has(cycleId)
      ) {
        return;
      }
      return snapshot;
    },
    [agencyId, queryClient]
  );

  const getEntry = useCallback(
    (scope: DraftReadinessScope, cycleId: string) =>
      currentSnapshot(scope, cycleId)?.entries.get(cycleId),
    [currentSnapshot]
  );

  const isScopeAdmitted = useCallback(
    (scope: DraftReadinessScope, cycleId: string) =>
      currentSnapshot(scope, cycleId, true) !== undefined,
    [currentSnapshot]
  );

  /**
   * Record a draft tab's current list scope.
   *
   * Reads the exact Query state so a success that happened before this provider
   * mounted is still adopted. An unchanged settled registration must not
   * replace the snapshot (that would drop its evidence); a different query hash
   * replaces it. Unmounting a tab does not call this with a deletion.
   */
  const registerList = useCallback(
    (scope: DraftReadinessScope) => {
      const query = queryClient.getQueryCache().find({
        queryKey: scope.queryKey,
        exact: true,
      });
      if (!query) return;
      const { queryHash, state } = query;
      const adoptable =
        !scope.isPlaceholderData &&
        state.status === 'success' &&
        state.fetchStatus === 'idle' &&
        !state.isInvalidated;

      const existing = snapshots.current[scope.tabId];
      if (
        existing &&
        existing.queryHash === queryHash &&
        existing.dataUpdateCount === state.dataUpdateCount &&
        existing.available === adoptable
      ) {
        return;
      }

      snapshots.current[scope.tabId] = adoptable
        ? {
            queryHash,
            queryKey: scope.queryKey,
            dataUpdateCount: state.dataUpdateCount,
            available: true,
            rowIds: collectRowIds(state.data),
            entries: new Map(),
          }
        : {
            queryHash,
            queryKey: scope.queryKey,
            dataUpdateCount: state.dataUpdateCount,
            available: false,
            rowIds: new Set(),
            entries: new Map(),
          };
      notify();
    },
    [notify, queryClient]
  );

  const loadDetails = useCallback(
    (scope: DraftReadinessScope, cycleId: string, retry = false) => {
      const snapshot = currentSnapshot(scope, cycleId);
      if (!snapshot) return;

      const { tabId } = scope;
      const existing = snapshot.entries.get(cycleId);
      // Loading and success entries deduplicate; a plain call never replaces an
      // error entry — Retry (`retry: true`) is the explicit replacement path.
      if (existing && !retry) return existing.promise;

      const entry: DetailEntry = { status: 'loading' };
      snapshot.entries.set(cycleId, entry);

      const isCurrent = () =>
        alive.current &&
        snapshots.current[tabId] === snapshot &&
        snapshot.entries.get(cycleId) === entry;

      entry.promise = getBillingCycleReadinessDetails({
        path: { billing_cycle_id: cycleId },
      })
        .then(response => {
          if (response.error || !response.data) {
            throw new Error('Readiness details unavailable');
          }
          if (response.data.billing_cycle_id !== cycleId) {
            throw new Error('Unexpected cycle');
          }
          if (isCurrent()) {
            entry.status = 'success';
            entry.data = response.data;
            notify();
          }
        })
        .catch(error => {
          console.error('Failed to load readiness details', error);
          if (isCurrent()) {
            entry.status = 'error';
            notify();
          }
        });

      notify();
      return entry.promise;
    },
    [currentSnapshot, notify]
  );

  useEffect(() => {
    alive.current = true;

    const unsubscribe = queryClient.getQueryCache().subscribe(event => {
      for (const tabId of DRAFT_READINESS_TABS) {
        const snapshot = snapshots.current[tabId];
        if (!snapshot || snapshot.queryHash !== event.query.queryHash) continue;

        if (event.type === 'removed') {
          snapshot.available = false;
          snapshot.rowIds = new Set();
          snapshot.entries = new Map();
          notify();
          continue;
        }
        if (event.type !== 'updated') continue;

        const { action } = event;
        if (action.type === 'success') {
          snapshot.dataUpdateCount = event.query.state.dataUpdateCount;
          snapshot.rowIds = collectRowIds(event.query.state.data);
          snapshot.entries = new Map();
          snapshot.available = true;
          notify();
        } else if (action.type === 'error') {
          snapshot.available = false;
          snapshot.rowIds = new Set();
          snapshot.entries = new Map();
          notify();
        } else if (action.type === 'fetch' || action.type === 'invalidate') {
          snapshot.available = false;
          snapshot.entries = new Map();
          notify();
        }
      }
    });

    // Reconcile the snapshots a child registered before this effect ran, then
    // notify so a consumer can retry a read that was unavailable at registration.
    for (const tabId of DRAFT_READINESS_TABS) {
      const snapshot = snapshots.current[tabId];
      if (!snapshot) continue;
      const query = queryClient.getQueryCache().find({
        queryKey: snapshot.queryKey,
        exact: true,
      });
      if (!query || query.queryHash !== snapshot.queryHash) {
        snapshot.available = false;
        continue;
      }
      const { state } = query;
      const adoptable =
        state.status === 'success' &&
        state.fetchStatus === 'idle' &&
        !state.isInvalidated;
      if (adoptable) {
        if (
          !snapshot.available ||
          snapshot.dataUpdateCount !== state.dataUpdateCount
        ) {
          snapshot.dataUpdateCount = state.dataUpdateCount;
          snapshot.rowIds = collectRowIds(state.data);
          snapshot.entries = new Map();
          snapshot.available = true;
        }
      } else {
        snapshot.available = false;
        snapshot.entries = new Map();
      }
    }
    notify();

    return () => {
      unsubscribe();
      alive.current = false;
      // Clearing snapshots keeps pre-cleanup responses from becoming current
      // when setup restores the live lifetime.
      snapshots.current = emptySnapshots();
    };
  }, [agencyId, notify, queryClient]);

  const value = useMemo(
    () => ({ revision, registerList, getEntry, isScopeAdmitted, loadDetails }),
    [revision, registerList, getEntry, isScopeAdmitted, loadDetails]
  );

  return (
    <ClaimReadinessContext.Provider value={value}>
      {children}
    </ClaimReadinessContext.Provider>
  );
};
